// Customer Analytics Tracking Utility
// Tracks user interactions throughout the website

import { supabase } from '../admin/lib/supabaseClient';

interface UserInteraction {
  session_id: string;
  customer_email?: string;
  interaction_type: 'page_view' | 'click' | 'form_submit' | 'button_click' | 'link_click' | 'scroll' | 'hover' | 'product_view' | 'add_to_cart' | 'search' | 'download' | 'contact' | 'error' | 'performance';
  page_url: string;
  page_title?: string;
  element_clicked?: string;
  element_text?: string;
  element_class?: string;
  element_id?: string;
  metadata?: Record<string, any>;
  user_agent?: string;
  referrer?: string;
  screen_resolution?: string;
  duration_seconds?: number;
}

class AnalyticsTracker {
  private sessionId: string;
  private pageStartTime: number;
  private isTrackingEnabled: boolean;
  private customerEmail?: string;

  // Persistent local queue and helpers for reliable delivery
  private eventQueue: UserInteraction[] = [];
  private readonly QUEUE_KEY = 'analytics_event_queue';
  private flushing = false;
  private lastClickTimestamps: Record<string, number> = {};

  constructor() {
    const DEBUG = () => localStorage.getItem('ANALYTICS_DEBUG') === '1' || localStorage.getItem('AUCTION_DEBUG') === '1';
    this.sessionId = this.generateSessionId();
    this.pageStartTime = Date.now();
    this.isTrackingEnabled = true;

    // Load any persisted queue
    try {
      const raw = localStorage.getItem(this.QUEUE_KEY);
      if (raw) this.eventQueue = JSON.parse(raw) as UserInteraction[];
    } catch (e) {
      console.warn('Failed to load analytics queue:', e);
      this.eventQueue = [];
    }

  // Track page view on initialization
    this.trackPageView();

    // Set up event listeners
    this.setupEventListeners();
    
    // Set up error tracking
    this.setupErrorTracking();
    
    // Track performance metrics after page load
    if (document.readyState === 'complete') {
      this.trackPerformanceMetrics();
    } else {
      window.addEventListener('load', () => this.trackPerformanceMetrics());
    }

    // Flush queue when connection is restored
    window.addEventListener('online', () => this.flushQueue());

    // Periodic flush (in case online event missed)
    setInterval(() => this.flushQueue(), 30_000);

    // Expose minimal debug helpers in dev/when enabled
    if (DEBUG()) {
      (window as any).analyticsDebug = {
        queueSize: () => this.eventQueue.length,
        peek: (n = 5) => this.eventQueue.slice(0, n),
        flush: () => this.flushQueue(),
        sessionId: () => this.sessionId
      };
      console.info('[ANALYTICS DEBUG] Enabled. window.analyticsDebug available.');
    }
  }

  private generateSessionId(): string {
    // Use localStorage so multiple tabs share the same visitor session id
    try {
      const existing = localStorage.getItem('analytics_session_id');
      if (existing) return existing;
    } catch (e) {
      // fallback to sessionStorage if localStorage blocked
      const existingSession = sessionStorage.getItem('analytics_session_id');
      if (existingSession) return existingSession;
    }

    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    try {
      localStorage.setItem('analytics_session_id', newSessionId);
    } catch (e) {
      sessionStorage.setItem('analytics_session_id', newSessionId);
    }
    return newSessionId;
  }

  private setupEventListeners(): void {
    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.trackPageLeave();
      } else {
        this.trackPageView();
      }
    });

    // Track page unload
    window.addEventListener('beforeunload', () => {
      this.trackPageLeave();
    });

    // Track clicks on important elements (with intelligent filtering and throttle)
    document.addEventListener('click', (event) => {
      try {
        const target = event.target as Element;
        if (!target) return;
        
        // Filter out meaningless clicks
        if (!this.shouldTrackClick(target)) return;
        
        const info = this.getElementInfo(target);
        const key = `${info.element_tag}-${info.element_id || ''}-${(info.element_class || '').split(' ')[0] || ''}`;
        const now = Date.now();
        const last = this.lastClickTimestamps[key] || 0;
        if (now - last < 400) return; // ignore clicks on same element within 400ms
        this.lastClickTimestamps[key] = now;
        
        this.trackClick(event);
      } catch (e) {
        // ignore throttle errors
      }
    }, true);

    // Track form submissions
    document.addEventListener('submit', (event) => {
      this.trackFormSubmit(event);
    }, true);

    // Track scroll to bottom
    let hasScrolledToBottom = false;
    window.addEventListener('scroll', () => {
      if (!hasScrolledToBottom && 
          (window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100) {
        hasScrolledToBottom = true;
        this.trackEvent('scroll', 'Scrolled to bottom', { scroll_depth: '100%' });
      }
    });
  }

  private async saveInteraction(interaction: UserInteraction): Promise<void> {
    if (!this.isTrackingEnabled) return;
    // Add a unique event id with better uniqueness (timestamp + random + counter)
    const eventId = `evt_${Date.now()}_${Math.random().toString(36).substring(2, 15)}_${this.eventQueue.length}`;
    const queued: UserInteraction = {
      ...interaction,
      metadata: {
        ...(interaction.metadata || {}),
        event_id: eventId,
        timestamp: new Date().toISOString()
      }
    } as UserInteraction;

    // Push to local queue and persist
    this.eventQueue.push(queued);
    try {
      localStorage.setItem(this.QUEUE_KEY, JSON.stringify(this.eventQueue));
    } catch (e) {
      console.warn('Failed to persist analytics queue:', e);
    }

    // Try to flush immediately (non-blocking)
    this.flushQueue().catch((err) => console.warn('Flush queue failed:', err));
  }

  // Send queued events in batches with retry logic
  private async flushQueue(): Promise<void> {
    if (this.flushing) return;
    if (!navigator.onLine) return; // avoid attempting when offline

    if (this.eventQueue.length === 0) return;
    this.flushing = true;

    try {
      // send in batches of 10
      const BATCH = 10;
      const DEBUG = localStorage.getItem('ANALYTICS_DEBUG') === '1' || localStorage.getItem('AUCTION_DEBUG') === '1';
      while (this.eventQueue.length > 0) {
        const batch = this.eventQueue.slice(0, BATCH);
        const payload = batch.map((interaction) => ({
          session_id: interaction.session_id,
          customer_email: interaction.customer_email,
          interaction_type: interaction.interaction_type,
          page_url: interaction.page_url,
          page_title: interaction.page_title,
          element_clicked: interaction.element_clicked,
          metadata: interaction.metadata || {},
          duration_seconds: interaction.duration_seconds,
          created_at: (interaction.metadata && (interaction.metadata as any).timestamp) || new Date().toISOString()
        }));

        if (DEBUG) {
          console.groupCollapsed('[ANALYTICS DEBUG] Flushing analytics batch');
          console.log('count:', payload.length);
          console.log('first item payload:', payload[0]);
          console.log('navigator.onLine:', navigator.onLine);
          console.log('supabase configured:', !!supabase);
          console.groupEnd();
        }

        let error;
        let result;
        try {
          result = await supabase.from('customer_interactions').insert(payload);
          error = result.error;
          
          if (DEBUG && error) {
            console.group('[ANALYTICS DEBUG] Supabase returned error');
            console.log('error.message:', error.message);
            console.log('error.code:', error.code);
            console.log('error.details:', (error as any).details);
            console.log('error.hint:', (error as any).hint);
            console.log('full error object:', error);
            console.groupEnd();
          }
        } catch (networkErr: any) {
          // Catch network-level errors (fetch failures, DNS, timeouts)
          if (DEBUG) {
            console.group('[ANALYTICS DEBUG] Network-level exception');
            console.log('error type:', networkErr?.name);
            console.log('error message:', networkErr?.message);
            console.log('error stack:', networkErr?.stack);
            console.log('full exception:', networkErr);
            console.groupEnd();
          }
          error = { message: `Network error: ${networkErr?.message}`, code: 'NETWORK_ERROR', details: '', hint: '' };
        }
        if (error) {
          // If it's a duplicate key error (409/23505), just skip these events
          if (error.code === '23505' || error.message?.includes('duplicate key')) {
            console.debug('Skipping duplicate analytics events');
            this.eventQueue.splice(0, batch.length); // Remove duplicates from queue
          } else {
            // For other errors, stop and retry later
            if (DEBUG) {
              console.group('[ANALYTICS DEBUG] Insert error');
              console.log('error.message:', error.message);
              console.log('error.code:', error.code);
              console.log('error.details:', (error as any).details);
              console.log('error.hint:', (error as any).hint);
              console.groupEnd();
            } else {
              console.warn('Analytics batch insert error:', error);
            }
            break;
          }
        } else {
          // remove sent events from queue
          this.eventQueue.splice(0, batch.length);
        }
        try {
          localStorage.setItem(this.QUEUE_KEY, JSON.stringify(this.eventQueue));
        } catch (e) {
          console.warn('Failed to persist analytics queue after flush:', e);
        }
      }
    } catch (e) {
      console.warn('Error flushing analytics queue:', e);
    } finally {
      this.flushing = false;
    }
  }

  private setupErrorTracking(): void {
    // Track JavaScript errors
    window.addEventListener('error', (event) => {
      this.trackEvent('error', 'JavaScript Error', {
        error_message: event.message,
        error_file: event.filename,
        error_line: event.lineno,
        error_col: event.colno,
        error_stack: event.error?.stack?.substring(0, 500) // Limit stack trace size
      });
    });
    
    // Track unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.trackEvent('error', 'Unhandled Promise Rejection', {
        error_reason: event.reason?.toString(),
        error_stack: event.reason?.stack?.substring(0, 500)
      });
    });
  }

  private trackPerformanceMetrics(): void {
    if (!window.performance || !window.performance.timing) return;
    
    try {
      const timing = window.performance.timing;
      const pageLoadTime = timing.loadEventEnd - timing.navigationStart;
      const dnsTime = timing.domainLookupEnd - timing.domainLookupStart;
      const tcpTime = timing.connectEnd - timing.connectStart;
      const ttfb = timing.responseStart - timing.navigationStart;
      const domContentLoaded = timing.domContentLoadedEventEnd - timing.navigationStart;
      
      this.trackEvent('performance', 'Page Load Metrics', {
        page_load_time: pageLoadTime,
        dns_time: dnsTime,
        tcp_time: tcpTime,
        time_to_first_byte: ttfb,
        dom_content_loaded: domContentLoaded,
        dom_interactive: timing.domInteractive - timing.navigationStart,
        dom_complete: timing.domComplete - timing.navigationStart
      });
    } catch (e) {
      console.warn('Failed to track performance metrics:', e);
    }
  }

  private getPageInfo() {
    return {
      page_url: window.location.href,
      page_title: document.title,
      user_agent: navigator.userAgent,
      referrer: document.referrer,
      screen_resolution: `${screen.width}x${screen.height}`,
      ...this.captureUTMParameters()
    };
  }

  private shouldTrackClick(element: Element): boolean {
    // Skip tracking for admin interface
    if (window.location.pathname.includes('/admin')) return false;
    
    // Skip meaningless container elements
    const skipTags = ['HTML', 'BODY', 'DIV', 'SPAN', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'NAV', 'MAIN'];
    if (skipTags.includes(element.tagName)) return false;
    
    // Skip elements with data-no-track attribute
    if (element.hasAttribute('data-no-track') || element.closest('[data-no-track]')) return false;
    
    // Only track meaningful interactions
    const trackTags = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'IMG'];
    const hasInteractiveRole = ['button', 'link', 'menuitem', 'tab'].includes(element.getAttribute('role') || '');
    const isClickable = element.classList.contains('clickable') || element.classList.contains('button');
    
    return trackTags.includes(element.tagName) || hasInteractiveRole || isClickable;
  }

  private captureUTMParameters(): Record<string, string> {
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    utmKeys.forEach(key => {
      const value = params.get(key);
      if (value) utm[key] = value;
    });
    
    return utm;
  }

  private getElementInfo(element: Element) {
    return {
      element_text: element.textContent?.trim().substring(0, 100),
      element_class: element.className,
      element_id: element.id,
      element_tag: element.tagName.toLowerCase()
    };
  }

  // Public methods for tracking specific events

  async trackPageView(): Promise<void> {
    this.pageStartTime = Date.now();
    
    await this.saveInteraction({
      session_id: this.sessionId,
      customer_email: this.customerEmail,
      interaction_type: 'page_view',
      ...this.getPageInfo()
    });
  }

  async trackPageLeave(): Promise<void> {
    const duration = Math.round((Date.now() - this.pageStartTime) / 1000);
    
    await this.saveInteraction({
      session_id: this.sessionId,
      customer_email: this.customerEmail,
      interaction_type: 'page_view',
      duration_seconds: duration,
      ...this.getPageInfo()
    });
  }

  async trackClick(event: MouseEvent): Promise<void> {
    const target = event.target as Element;
    if (!target) return;

    // Skip tracking for admin interface
    if (window.location.pathname.includes('/admin')) return;

    const elementInfo = this.getElementInfo(target);
    let interactionType: UserInteraction['interaction_type'] = 'click';

    // Determine specific interaction type
    if (target.tagName === 'BUTTON') {
      interactionType = 'button_click';
    } else if (target.tagName === 'A') {
      interactionType = 'link_click';
    }

    await this.saveInteraction({
      session_id: this.sessionId,
      customer_email: this.customerEmail,
      interaction_type: interactionType,
      element_clicked: `${elementInfo.element_tag}${elementInfo.element_id ? '#' + elementInfo.element_id : ''}${elementInfo.element_class ? '.' + elementInfo.element_class.split(' ')[0] : ''}`,
      ...this.getPageInfo(),
      ...elementInfo
    });
  }

  async trackFormSubmit(event: SubmitEvent): Promise<void> {
    const form = event.target as HTMLFormElement;
    if (!form) return;

    const formInfo = this.getElementInfo(form);

    await this.saveInteraction({
      session_id: this.sessionId,
      customer_email: this.customerEmail,
      interaction_type: 'form_submit',
      element_clicked: `form${formInfo.element_id ? '#' + formInfo.element_id : ''}`,
      ...this.getPageInfo(),
      metadata: {
        form_action: form.action,
        form_method: form.method,
        form_elements: form.elements.length
      }
    });
  }

  async trackEvent(
    type: UserInteraction['interaction_type'], 
    elementDescription: string, 
    metadata?: Record<string, any>
  ): Promise<void> {
    await this.saveInteraction({
      session_id: this.sessionId,
      customer_email: this.customerEmail,
      interaction_type: type,
      element_clicked: elementDescription,
      ...this.getPageInfo(),
      metadata
    });
  }

  // Track specific business events

  async trackProductView(productId: string, productName: string): Promise<void> {
    await this.trackEvent('product_view', `Product: ${productName}`, {
      product_id: productId,
      product_name: productName
    });
  }

  async trackAddToCart(productId: string, productName: string, quantity: number, price: number): Promise<void> {
    await this.trackEvent('add_to_cart', `Add to cart: ${productName}`, {
      product_id: productId,
      product_name: productName,
      quantity,
      price
    });
  }

  async trackSearch(searchTerm: string, results: number): Promise<void> {
    await this.trackEvent('search', `Search: ${searchTerm}`, {
      search_term: searchTerm,
      results_count: results
    });
  }

  async trackContactInquiry(contactType: string): Promise<void> {
    await this.trackEvent('contact', `Contact: ${contactType}`, {
      contact_type: contactType
    });
  }

  async trackCarInquiry(carId: string, carTitle: string): Promise<void> {
    await this.trackEvent('contact', `Car inquiry: ${carTitle}`, {
      car_id: carId,
      car_title: carTitle,
      inquiry_type: 'car'
    });
  }

  async trackConversion(
    conversionType: 'purchase' | 'contact' | 'quote' | 'car_inquiry' | 'auction_registration' | 'auction_bid',
    conversionValue?: number,
    metadata?: Record<string, any>
  ): Promise<void> {
    await this.trackEvent('contact', `Conversion: ${conversionType}`, {
      conversion_type: conversionType,
      conversion_value: conversionValue,
      ...metadata
    });
  }

  async trackFunnelStep(
    funnelName: 'checkout' | 'contact' | 'car_inquiry' | 'auction_bid',
    step: string,
    stepData?: Record<string, any>
  ): Promise<void> {
    await this.trackEvent('form_submit', `${funnelName}: ${step}`, {
      funnel_name: funnelName,
      step_name: step,
      ...stepData
    });
  }

  // Set customer email for logged-in users
  setCustomerEmail(email: string): void {
    this.customerEmail = email;
  }

  // Enable/disable tracking
  setTrackingEnabled(enabled: boolean): void {
    this.isTrackingEnabled = enabled;
  }

  // Get session ID for external use
  getSessionId(): string {
    return this.sessionId;
  }
}

// Create global tracker instance
const analyticsTracker = new AnalyticsTracker();

// Export convenience functions
export const trackPageView = () => analyticsTracker.trackPageView();
export const trackEvent = (type: UserInteraction['interaction_type'], description: string, metadata?: Record<string, any>) => 
  analyticsTracker.trackEvent(type, description, metadata);
export const trackProductView = (productId: string, productName: string) => 
  analyticsTracker.trackProductView(productId, productName);
export const trackAddToCart = (productId: string, productName: string, quantity: number, price: number) => 
  analyticsTracker.trackAddToCart(productId, productName, quantity, price);
export const trackSearch = (searchTerm: string, results: number) => 
  analyticsTracker.trackSearch(searchTerm, results);
export const trackContactInquiry = (contactType: string) => 
  analyticsTracker.trackContactInquiry(contactType);
export const trackCarInquiry = (carId: string, carTitle: string) => 
  analyticsTracker.trackCarInquiry(carId, carTitle);
export const trackConversion = (type: 'purchase' | 'contact' | 'quote' | 'car_inquiry' | 'auction_registration' | 'auction_bid', value?: number, metadata?: Record<string, any>) =>
  analyticsTracker.trackConversion(type, value, metadata);
export const trackFunnelStep = (funnel: 'checkout' | 'contact' | 'car_inquiry' | 'auction_bid', step: string, data?: Record<string, any>) =>
  analyticsTracker.trackFunnelStep(funnel, step, data);
export const setCustomerEmail = (email: string) => 
  analyticsTracker.setCustomerEmail(email);
export const setTrackingEnabled = (enabled: boolean) => 
  analyticsTracker.setTrackingEnabled(enabled);
export const getSessionId = () => 
  analyticsTracker.getSessionId();

export default analyticsTracker;
